import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Drawer,
  useMediaQuery,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { useDispatch } from "react-redux";

import { PATH_DASHBOARD } from "@app/routes/paths";
import { logout } from "@app/modules/auth";
import { imageSource, Strings } from "@app/constants";
import React from "react";
import { SvgColor } from "../svg-color";

const renderImageIcon = (name: string) => (
  <SvgColor src={name} sx={{ width: 20, height: 20, color: "#393939" }} />
);

const menuItems = [
  {
    label: "Dashboard",
    icon: renderImageIcon(imageSource.iconDashboard),
    path: PATH_DASHBOARD.dashboard,
  },
  {
    label: "Cars",
    icon: renderImageIcon(imageSource.iconProducts),
    path: PATH_DASHBOARD.product,
  },
  {
    label: "Customers",
    icon: renderImageIcon(imageSource.iconCustomers),
    path: PATH_DASHBOARD.customerPage,
  },
  {
    label: "Brand",
    icon: renderImageIcon(imageSource.iconBrand),
    path: PATH_DASHBOARD.brand,
  },
  {
    label: "Model",
    icon: renderImageIcon(imageSource.iconModel),
    path: PATH_DASHBOARD.model,
  },
  {
    label: "User",
    icon: renderImageIcon(imageSource.iconUser),
    path: PATH_DASHBOARD.salesPerson,
  },
  {
    label: "Requests",
    icon: renderImageIcon(imageSource.iconRequest),
    path: PATH_DASHBOARD.requests,
  },
];

const Sidebar = ({
  activeItem,
  setActiveItem,
  drawerOpen,
  setDrawerOpen,
}: any) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleItemClick = (label: string, path?: string) => {
    setActiveItem(label);
    if (path) {
      navigate(path);
    }
    if (isMobile && setDrawerOpen) setDrawerOpen(false);
  };

  const renderMenuList = () => (
    <Box
      sx={{
        pr: 1,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <Box>
        {isMobile && (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mx: 2, my: 3 }}>
            <img
              src="/assets/images/kaizen-motor.png"
              alt="Logo"
              style={{ width: 'auto'}}
            />
          
          </Box>
        )}

        <List>
          {menuItems.map(({ label, icon, path }) => {
            const isActive = activeItem === label;
            return (
              <ListItemButton
                key={label}
                onClick={() => handleItemClick(label, path)}
                sx={{
                  mb: 1,
                  borderRadius: 1,
                  backgroundColor: isActive
                    ? theme.palette.primary.main
                    : "transparent",
                  color: isActive ? "#fff" : "#90A4AE",
                  px: 1.5,
                  "&:hover": {
                    backgroundColor: isActive
                      ? theme.palette.primary.main
                      : "#f0f0f0",
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: isActive ? "#fff" : "#90A4AE",
                    minWidth: "auto",
                    mr: 0.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: isActive ? "#fff" : "#90A4AE",
                      minWidth: "auto",
                      mr: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {typeof icon === "string" ? (
                      <Icon
                        icon={icon}
                        width={20}
                        height={20}
                        color={isActive ? "#ffffff" : "#90A4AE"}
                      />
                    ) : (
                      React.cloneElement(icon, {
                        style: {
                          ...icon.props.style,
                          filter: isActive ? "brightness(0) invert(1)" : "none",
                        },
                      })
                    )}
                  </ListItemIcon>
                </ListItemIcon>
                <ListItemText
                  primary={label}
                  primaryTypographyProps={{
                    sx: {
                      color: isActive ? "#fff" : "#393939",
                      fontFamily: "Plus Jakarta Sans",
                      fontWeight: 500,
                    },
                  }}
                />
              </ListItemButton>
            );
          })}
        </List>
      </Box>

      {/* Bottom Section: Logout Button */}
      <Box sx={{ px: 1, pt: 2 }}>
        <ListItemButton
          onClick={() => dispatch(logout())}
          sx={{
            color: "red",
            borderRadius: 1,
          }}
        >
          <ListItemIcon sx={{ color: "red", minWidth: 0, mr: 1 }}>
            <Icon
              icon="solar:logout-2-outline"
              width={20}
              height={20}
              color="#FF0004"
            />
          </ListItemIcon>
          <ListItemText
            primary={Strings.common.logOut}
            sx={{
              color: "#FF0004",
              fontFamily: "Plus Jakarta Sans",
              fontWeight: 500,
            }}
          />
        </ListItemButton>
      </Box>
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: 240,
            backgroundColor: theme.palette.common.white,
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Box
          sx={{
            height: "100vh",
            overflowY: "auto",
            p: 2,
          }}
        >
          {renderMenuList()}
        </Box>
      </Drawer>
    );
  }
  return (
    <Box
      sx={{
        width: 240,
        backgroundColor: theme.palette.common.white,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          height: "100vh",
          overflowY: "auto",
          p: 2,
        }}
      >
        {renderMenuList()}
      </Box>
    </Box>
  );
};

export { Sidebar };
